<template>
  <div class="portal-page" :style="pageStyle">
    <header class="portal-header">
      <div class="portal-container portal-header-inner">
        <div class="portal-brand">
          <img
            v-if="logoUrl && !logoError"
            :src="logoUrl"
            :alt="appName"
            class="portal-logo"
            @error="logoError = true"
          />
          <span v-else class="portal-brand-text">{{ appName }}</span>
        </div>
        <router-link :to="loginPath" class="portal-btn portal-btn-outline">
          Sign In
        </router-link>
      </div>
    </header>

    <section class="portal-hero">
      <div class="portal-container portal-hero-inner">
        <p class="portal-eyebrow">Member Portal · Powered by Veterinary Media.ai</p>
        <h1>{{ appName }}</h1>
        <p class="portal-tagline">{{ tagline }}</p>
        <div class="portal-actions">
          <router-link :to="loginPath" class="portal-btn portal-btn-primary">
            Member Sign In
          </router-link>
          <router-link :to="registerPath" class="portal-btn portal-btn-secondary">
            Create Account
          </router-link>
        </div>
      </div>
    </section>

    <AssociationPortalConsultDemo
      :org-slug="orgSlug || ''"
      :short-name="shortName"
    />

    <section class="portal-features">
      <div class="portal-container portal-features-grid">
        <article class="portal-feature-card">
          <div class="portal-feature-icon">
            <i class="fas fa-graduation-cap" aria-hidden="true"></i>
          </div>
          <h2>Continuing Education</h2>
          <p>
            Browse on-demand CE courses, track completions, and download certificates
            — all in one place for {{ shortName }} members.
          </p>
          <router-link :to="learningPath" class="portal-link">
            Explore courses <i class="fas fa-arrow-right" aria-hidden="true"></i>
          </router-link>
        </article>

        <article class="portal-feature-card">
          <div class="portal-feature-icon">
            <i class="fas fa-stethoscope" aria-hidden="true"></i>
          </div>
          <h2>Full Clinical Agent</h2>
          <p>
            Sign in for unlimited cited consults, session history, deep research mode,
            and related CE surfaced alongside every answer.
          </p>
          <a href="#try-consult" class="portal-link">
            Try a sample question <i class="fas fa-arrow-up" aria-hidden="true"></i>
          </a>
          <router-link :to="consultPath" class="portal-link portal-link-secondary">
            Sign in for unlimited <i class="fas fa-arrow-right" aria-hidden="true"></i>
          </router-link>
        </article>

        <article class="portal-feature-card">
          <div class="portal-feature-icon">
            <i class="fas fa-id-badge" aria-hidden="true"></i>
          </div>
          <h2>Member Benefits</h2>
          <p>
            Verified members may receive association CE discounts and complimentary course
            benefits configured by {{ shortName }}.
          </p>
          <router-link :to="loginPath" class="portal-link">
            Sign in to verify <i class="fas fa-arrow-right" aria-hidden="true"></i>
          </router-link>
        </article>
      </div>
    </section>

    <footer class="portal-footer">
      <div class="portal-container portal-footer-inner">
        <p>{{ footerText }}</p>
        <a
          v-if="associationWebsite"
          :href="associationWebsite"
          target="_blank"
          rel="noopener noreferrer"
          class="portal-footer-link"
        >
          Visit {{ shortName }} website
          <i class="fas fa-external-link-alt" aria-hidden="true"></i>
        </a>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import AssociationPortalConsultDemo from '@/components/lms/AssociationPortalConsultDemo.vue';
import whitelabelBranding from '@/services/whitelabelBrandingService';
import {
  getVmaeDemoOrgSlug,
  loginPathForOrg,
  registerPathForOrg,
} from '@/utils/associationPortal';

const logoError = ref(false);

const branding = computed(() => whitelabelBranding.getCurrentBranding() || {});
const orgSlug = computed(() => getVmaeDemoOrgSlug());

const logoUrl = computed(() => whitelabelBranding.getLogo());
const appName = computed(() => whitelabelBranding.getAppName() || 'Member Portal');
const tagline = computed(
  () => branding.value.tagline || 'Your daily hub for CE, clinical reference, and member engagement.'
);
const shortName = computed(
  () => branding.value.association_meta?.short_name || appName.value
);
const associationWebsite = computed(() => branding.value.association_meta?.website || '');
const footerText = computed(
  () => whitelabelBranding.getFooterText() || `© ${appName.value} · Powered by Veterinary Media.ai`
);

const pageStyle = computed(() => ({
  '--portal-primary': whitelabelBranding.getColor('primary'),
  '--portal-secondary': whitelabelBranding.getColor('secondary'),
  '--portal-accent': whitelabelBranding.getColor('accent'),
  '--portal-text': whitelabelBranding.getColor('text'),
  '--portal-surface': whitelabelBranding.getColor('surface'),
}));

const loginPath = computed(() => loginPathForOrg(orgSlug.value));
const registerPath = computed(() => registerPathForOrg(orgSlug.value));
const learningPath = computed(() => (orgSlug.value ? `/learning?org=${orgSlug.value}` : '/learning'));
const consultPath = computed(() => {
  if (orgSlug.value) {
    return `/learning?tab=agent&org=${orgSlug.value}`;
  }
  return '/learning?tab=agent';
});
</script>

<style scoped>
.portal-page {
  min-height: 100vh;
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--portal-primary) 8%, #ffffff) 0%,
    #ffffff 240px
  );
  color: var(--portal-text, #0f1f3d);
}

.portal-container {
  width: min(1120px, calc(100% - 2rem));
  margin: 0 auto;
}

.portal-header {
  position: sticky;
  top: 0;
  z-index: 10;
  backdrop-filter: blur(10px);
  background: color-mix(in srgb, var(--portal-surface, #fff) 88%, transparent);
  border-bottom: 1px solid color-mix(in srgb, var(--portal-primary) 12%, #e5e7eb);
}

.portal-header-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 0;
}

.portal-logo {
  max-height: 52px;
  max-width: min(280px, 60vw);
  object-fit: contain;
}

.portal-brand-text {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--portal-primary);
}

.portal-hero {
  padding: 4rem 0 3rem;
}

.portal-hero-inner {
  max-width: 760px;
}

.portal-eyebrow {
  margin: 0 0 0.75rem;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--portal-secondary);
}

.portal-hero h1 {
  margin: 0 0 1rem;
  font-size: clamp(2rem, 4vw, 3rem);
  line-height: 1.15;
  color: var(--portal-primary);
}

.portal-tagline {
  margin: 0 0 2rem;
  font-size: 1.15rem;
  line-height: 1.6;
  color: color-mix(in srgb, var(--portal-text, #0f1f3d) 78%, #6b7280);
}

.portal-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.portal-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.85rem 1.35rem;
  border-radius: 10px;
  font-weight: 600;
  text-decoration: none;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.portal-btn:hover {
  transform: translateY(-1px);
}

.portal-btn-primary {
  background: var(--portal-primary);
  color: #fff;
  box-shadow: 0 8px 24px color-mix(in srgb, var(--portal-primary) 28%, transparent);
}

.portal-btn-secondary {
  background: var(--portal-accent);
  color: #1f2937;
}

.portal-btn-outline {
  border: 1px solid color-mix(in srgb, var(--portal-primary) 24%, #cbd5e1);
  color: var(--portal-primary);
  background: #fff;
}

.portal-features {
  padding: 1rem 0 4rem;
}

.portal-features-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.25rem;
}

.portal-feature-card {
  background: var(--portal-surface, #fff);
  border: 1px solid color-mix(in srgb, var(--portal-primary) 10%, #e5e7eb);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 10px 30px color-mix(in srgb, var(--portal-primary) 6%, transparent);
}

.portal-feature-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  margin-bottom: 1rem;
  background: color-mix(in srgb, var(--portal-secondary) 14%, #fff);
  color: var(--portal-secondary);
  font-size: 1.1rem;
}

.portal-feature-card h2 {
  margin: 0 0 0.65rem;
  font-size: 1.15rem;
  color: var(--portal-primary);
}

.portal-feature-card p {
  margin: 0 0 1rem;
  line-height: 1.55;
  color: color-mix(in srgb, var(--portal-text, #0f1f3d) 72%, #6b7280);
}

.portal-link {
  color: var(--portal-secondary);
  font-weight: 600;
  text-decoration: none;
}

.portal-link i {
  margin-left: 0.35rem;
  font-size: 0.85rem;
}

.portal-link-secondary {
  display: inline-block;
  margin-top: 0.55rem;
}

.portal-footer {
  border-top: 1px solid #e5e7eb;
  padding: 1.5rem 0 2rem;
  background: #f8fafc;
}

.portal-footer-inner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.portal-footer p {
  margin: 0;
  color: #64748b;
  font-size: 0.92rem;
}

.portal-footer-link {
  color: var(--portal-primary);
  font-weight: 600;
  text-decoration: none;
}

@media (max-width: 900px) {
  .portal-features-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .portal-hero {
    padding-top: 2.5rem;
  }

  .portal-actions {
    flex-direction: column;
  }

  .portal-btn {
    width: 100%;
  }
}
</style>
